import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  Button,
  Input,
  Modal,
  Select,
  DatePicker,
  Typography,
  Radio,
} from 'antd';
import moment from 'moment';
// Constants
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
} from 'pages/CampaignDetails/CampaignDetails/components/TimelineDeliverablesModal/TimelineDeliverablesModal';
// Helpers
import {
  generateUgcDeliverableDefaultFormData,
  mapUgcEditingDeliverableToFormData,
  mapUgcDeliverableFormDataToTaskInput,
  ugcDeliverableChecklistValidation,
  UgcDeliverableFormData,
  DUE_DATE_OPTIONS,
  SINGLE_DATE_TYPE_OPTIONS,
} from 'helpers/campaignUgc';
import {
  createNumberOptions,
  getApprovalUnitOptions,
  findNumberOption,
  getDisabledDate,
} from 'helpers/timelineDeliverables';
import { ValueLabelOption } from 'helpers/campaignEditModal';
// Types
import { GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks } from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignMediaType,
  CbDueDateType,
  CbDisplayPeriod,
  CreateCampaignTaskV2Input,
} from 'api/graphql-global-types';
// Styles
import styles from './CustomContentDeliverableModal.module.scss';

const { TextArea } = Input;
const { Option } = Select;

type CustomContentDeliverableModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCampaignTaskV2Input) => void;
  editingItem?: GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks | null;
  /**
   * Maps the editing task into the form shape. Defaults to the custom-campaign
   * deliverable mapper; injectable to keep the modal flow-agnostic.
   */
  mapEditingItemToFormData?: (
    editingItem: GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks
  ) => UgcDeliverableFormData;
};

const CustomContentDeliverableModal: React.FC<
  CustomContentDeliverableModalProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  editingItem,
  mapEditingItemToFormData = mapUgcEditingDeliverableToFormData,
}) => {
  const wasOpenRef = useRef(false);

  const contentTaskOptions: ValueLabelOption[] = useMemo(
    () => [
      { label: 'Video', value: CampaignMediaType.VIDEO },
      { label: 'Image', value: CampaignMediaType.IMAGE },
    ],
    []
  );

  const approvalUnitOptions: ValueLabelOption[] = useMemo(
    () => getApprovalUnitOptions(),
    []
  );

  const defaultFormData = useMemo(
    () => generateUgcDeliverableDefaultFormData(),
    []
  );

  const initialValues = useMemo<UgcDeliverableFormData>(
    () =>
      editingItem ? mapEditingItemToFormData(editingItem) : defaultFormData,
    [editingItem, defaultFormData, mapEditingItemToFormData]
  );

  const { control, errors, handleSubmit, reset, watch, setValue } =
    useForm<UgcDeliverableFormData>({
      defaultValues: initialValues,
      resolver: yupResolver(ugcDeliverableChecklistValidation()),
      mode: 'onChange',
      reValidateMode: 'onChange',
      shouldUnregister: false,
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contentPieces',
  });

  // Seed the form on the closed -> open transition so editing an item loads
  // its saved values without clobbering edits while the modal stays open.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(initialValues);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialValues, reset]);

  const handleCloseModal = () => {
    reset(defaultFormData);
    onClose();
  };

  const handleValidSubmit = (formData: UgcDeliverableFormData) => {
    onSubmit(mapUgcDeliverableFormDataToTaskInput(formData));
    handleCloseModal();
  };

  const handleAddContentPiece = () => {
    append({
      mediaType: CampaignMediaType.VIDEO,
      title: '',
      description: '',
    });
  };

  const dueDateType = watch('dueDateType');
  const dateRangeType = watch('dateRangeType');
  const approvalUnit = watch('approvalUnit');
  const approvalNumber = watch('approvalNumber');

  const dueDateNotice =
    approvalNumber != null && approvalUnit != null
      ? `Content is due ${approvalNumber} ${
          (approvalUnit as string).toLowerCase().slice(0, -1) +
          ((approvalNumber as number) > 1 ? 's' : '')
        } after approval`
      : null;

  const approvalNumberOptions: ValueLabelOption[] = useMemo(
    () => createNumberOptions(approvalUnit === CbDisplayPeriod.DAYS ? 6 : 4),
    [approvalUnit]
  );

  return (
    <Modal
      title="Content Deliverable"
      visible={isOpen}
      onCancel={handleCloseModal}
      width={640}
      footer={null}
    >
      <form
        className={styles.modalContent}
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(handleValidSubmit)(event);
        }}
      >
        <div className={styles.formGroup}>
          <Typography.Text strong>Deliverable Name</Typography.Text>
          <Controller
            name="name"
            control={control}
            defaultValue={initialValues.name}
            render={({ onChange, value }) => (
              <Input
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Example: Outfit with Adi2000"
                maxLength={MAX_TITLE_LENGTH}
              />
            )}
          />
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            This is the name talent will see for this deliverable.
          </Typography.Text>
          {errors.name?.message && (
            <Typography.Text type="danger">
              {errors.name.message}
            </Typography.Text>
          )}
        </div>

        <div className={styles.sectionHeader}>
          <Typography.Title level={4}>Raw Content Needed</Typography.Title>
          <Button type="dashed" onClick={handleAddContentPiece}>
            + Add Content
          </Button>
        </div>

        {fields.map((field, index) => (
          <div key={field.id} className={styles.contentPiece}>
            <div className={styles.contentPieceHeader}>
              <Typography.Text strong>
                Content Piece {index + 1}
              </Typography.Text>
              <Button
                type="link"
                danger
                disabled={fields.length === 1}
                onClick={() => remove(index)}
              >
                Remove
              </Button>
            </div>

            <div className={styles.formGroup}>
              <Typography.Text strong>Media Type</Typography.Text>
              <Controller
                name={`contentPieces.${index}.mediaType`}
                control={control}
                defaultValue={
                  (field as any).mediaType ?? CampaignMediaType.VIDEO
                }
                render={({ onChange, value }) => (
                  <Select
                    value={value ?? CampaignMediaType.VIDEO}
                    onChange={onChange}
                    placeholder="Select type"
                    style={{ width: '100%' }}
                  >
                    {contentTaskOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.contentPieces?.[index]?.mediaType?.message && (
                <Typography.Text type="danger">
                  {errors.contentPieces[index]?.mediaType?.message}
                </Typography.Text>
              )}
            </div>

            <div className={styles.formGroup}>
              <Typography.Text strong>Content Description</Typography.Text>
              <Controller
                name={`contentPieces.${index}.title`}
                control={control}
                defaultValue={(field as any).title ?? ''}
                render={({ onChange, value }) => (
                  <Input
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Example: Dressing up clip"
                    maxLength={MAX_TITLE_LENGTH}
                  />
                )}
              />
              {errors.contentPieces?.[index]?.title?.message && (
                <Typography.Text type="danger">
                  {errors.contentPieces[index]?.title?.message}
                </Typography.Text>
              )}
            </div>

            <div className={styles.formGroup}>
              <Typography.Text strong>Instructions</Typography.Text>
              <Controller
                name={`contentPieces.${index}.description`}
                control={control}
                defaultValue={(field as any).description ?? ''}
                render={({ onChange, value }) => (
                  <TextArea
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    placeholder="Example: 30s vertical video where you are getting yourself dressed up."
                    maxLength={MAX_DESCRIPTION_LENGTH}
                  />
                )}
              />
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                Give talent clear instructions for this content including script
                guidance, framing, lighting, and key DOs & DON&apos;Ts.
              </Typography.Text>
              {errors.contentPieces?.[index]?.description?.message && (
                <Typography.Text type="danger">
                  {errors.contentPieces[index]?.description?.message}
                </Typography.Text>
              )}
            </div>
          </div>
        ))}

        <div className={styles.formGroup}>
          <Typography.Text strong>Content Due Date</Typography.Text>
          <Controller
            name="dueDateType"
            control={control}
            defaultValue={initialValues.dueDateType}
            render={({ onChange, value }) => (
              <Select
                value={value}
                onChange={onChange}
                placeholder="Select Content Deadline"
                style={{ width: '100%' }}
              >
                {DUE_DATE_OPTIONS.map((opt) => (
                  <Option key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            )}
          />
          {dueDateType === CbDueDateType.APPLICATION_BASED && dueDateNotice && (
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {dueDateNotice}
            </Typography.Text>
          )}
          {errors.dueDateType?.message && (
            <Typography.Text type="danger">
              {errors.dueDateType.message}
            </Typography.Text>
          )}
        </div>

        {dueDateType === CbDueDateType.APPLICATION_BASED && (
          <div className={styles.rowWrapper}>
            <div className={styles.formGroup}>
              <Typography.Text strong>Number</Typography.Text>
              <Controller
                name="approvalNumber"
                control={control}
                defaultValue={initialValues.approvalNumber}
                render={({ onChange, value }) => (
                  <Select
                    value={
                      findNumberOption(approvalNumberOptions, value)?.value
                    }
                    onChange={(nextValue) => onChange(Number(nextValue))}
                    placeholder="Select number"
                    style={{ width: '100%' }}
                  >
                    {approvalNumberOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.approvalNumber?.message && (
                <Typography.Text type="danger">
                  {errors.approvalNumber.message}
                </Typography.Text>
              )}
            </div>
            <div className={styles.formGroup}>
              <Typography.Text strong>Unit</Typography.Text>
              <Controller
                name="approvalUnit"
                control={control}
                defaultValue={initialValues.approvalUnit}
                render={({ onChange, value }) => (
                  <Select
                    value={value}
                    onChange={(nextValue) => {
                      onChange(nextValue);
                      if (value && value !== nextValue) {
                        setValue('approvalNumber', undefined, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    placeholder="Select unit"
                    style={{ width: '100%' }}
                  >
                    {approvalUnitOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.approvalUnit?.message && (
                <Typography.Text type="danger">
                  {errors.approvalUnit.message}
                </Typography.Text>
              )}
            </div>
          </div>
        )}

        {dueDateType === 'specific' && (
          <>
            <div className={styles.formGroup}>
              <Typography.Text strong>Date Type</Typography.Text>
              <Controller
                name="dateRangeType"
                control={control}
                defaultValue={initialValues.dateRangeType}
                render={({ onChange, value }) => (
                  <Radio.Group
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    <Radio value={CbDueDateType.FIXED}>Fixed Date</Radio>
                    <Radio value={CbDueDateType.RANGE}>Range</Radio>
                  </Radio.Group>
                )}
              />
              {errors.dateRangeType?.message && (
                <Typography.Text type="danger">
                  {errors.dateRangeType.message}
                </Typography.Text>
              )}
            </div>

            {dateRangeType === CbDueDateType.FIXED && (
              <div className={styles.rowWrapper}>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Timing</Typography.Text>
                  <Controller
                    name="singleDateType"
                    control={control}
                    defaultValue={initialValues.singleDateType}
                    render={({ onChange, value }) => (
                      <Select
                        value={value ?? undefined}
                        onChange={onChange}
                        placeholder="Select timing"
                        style={{ width: '100%' }}
                      >
                        {SINGLE_DATE_TYPE_OPTIONS.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.singleDateType?.message && (
                    <Typography.Text type="danger">
                      {errors.singleDateType.message}
                    </Typography.Text>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Date</Typography.Text>
                  <Controller
                    name="dueDate"
                    control={control}
                    defaultValue={initialValues.dueDate}
                    render={({ onChange, value }) => (
                      <DatePicker
                        value={value ? moment(value) : null}
                        onChange={(date) => onChange(date?.toDate())}
                        placeholder="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        disabledDate={getDisabledDate}
                        format="MMMM D, YYYY"
                      />
                    )}
                  />
                  {errors.dueDate?.message && (
                    <Typography.Text type="danger">
                      {errors.dueDate.message}
                    </Typography.Text>
                  )}
                </div>
              </div>
            )}

            {dateRangeType === CbDueDateType.RANGE && (
              <div className={styles.rowWrapper}>
                <div className={styles.formGroup}>
                  <Typography.Text strong>Start Date</Typography.Text>
                  <Controller
                    name="startDate"
                    control={control}
                    defaultValue={initialValues.startDate}
                    render={({ onChange, value }) => (
                      <DatePicker
                        value={value ? moment(value) : null}
                        onChange={(date) => onChange(date?.toDate())}
                        placeholder="Start Date"
                        style={{ width: '100%' }}
                        disabledDate={(current) =>
                          current && current < moment().startOf('day')
                        }
                        format="MMMM D, YYYY"
                      />
                    )}
                  />
                  {errors.startDate?.message && (
                    <Typography.Text type="danger">
                      {errors.startDate.message}
                    </Typography.Text>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <Typography.Text strong>End Date</Typography.Text>
                  <Controller
                    name="endDate"
                    control={control}
                    defaultValue={initialValues.endDate}
                    render={({ onChange, value }) => (
                      <DatePicker
                        value={value ? moment(value) : null}
                        onChange={(date) => onChange(date?.toDate())}
                        placeholder="End Date"
                        style={{ width: '100%' }}
                        disabledDate={(current) => {
                          if (!current) return false;
                          if (current < moment().startOf('day')) return true;
                          const startDate = watch('startDate');
                          return Boolean(
                            startDate &&
                              current < moment(startDate).startOf('day')
                          );
                        }}
                        format="MMMM D, YYYY"
                      />
                    )}
                  />
                  {errors.endDate?.message && (
                    <Typography.Text type="danger">
                      {errors.endDate.message}
                    </Typography.Text>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.formGroup}>
          <Typography.Text strong>Final Description</Typography.Text>
          <Controller
            name="description"
            control={control}
            defaultValue={initialValues.description}
            render={({ onChange, value }) => (
              <TextArea
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                rows={4}
                placeholder="Describe the final deliverable and any overall expectations for the talent."
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
            )}
          />
          {errors.description?.message && (
            <Typography.Text type="danger">
              {errors.description.message}
            </Typography.Text>
          )}
        </div>

        <div className={styles.actions}>
          <Button onClick={handleCloseModal}>Back</Button>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CustomContentDeliverableModal;
